import { call, put, select, takeLatest } from 'redux-saga/effects'
import * as Sentry from '@sentry/react'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import getAPIConfig from '@/shared/tools/getAPIConfig'

import {
  updateTeamsManagementCountrySites,
  updateTeamsManagementIsLoadingTeams,
  updateTeamsManagementTeams,
  updateTeamsManagementTeamsError,
} from './TeamsManagementSlice'
import { TeamApiResponse } from './types'

export const teamsManagementActions = {
  LOAD_LIST: 'LOAD_TEAMS_LIST',
  UPDATE_TEAM: 'UPDATE_TEAM',
  CREATE_TEAM: 'CREATE_TEAM',
  DELETE_TEAM: 'DELETE_TEAM',
}

interface AuthData {
  authTokenSession?: string
  lang?: string
  extraRoutes?: {
    teams?: {
      list?: string
      create?: string
      update?: string
      delete?: string
    }
  }
}

const buildUrl = (route: string, lang: string | undefined): string => {
  if (!route) return ''
  const safeLang = lang ?? 'fr'
  return route.includes('?') ? `${route}&lang=${safeLang}` : `${route}?lang=${safeLang}`
}

const doFetch = (url: string, config: any): Promise<Response> => fetch(url, config)

export function* loadTeamsSaga(): Generator {
  try {
    const { authTokenSession, lang, extraRoutes } = (yield select(selectAuthData)) as AuthData
    const listRoute = extraRoutes?.teams?.list
    if (!listRoute) return

    yield put(updateTeamsManagementIsLoadingTeams(true))
    yield put(updateTeamsManagementTeamsError({}))
    yield put(updateTeamsManagementTeams([]))

    const config = getAPIConfig({ method: 'get', authTokenSession, body: undefined })
    const response = (yield doFetch(buildUrl(listRoute, lang), config)) as Response

    const { status } = response
    const json = (yield call([response, response.json])) as TeamApiResponse
    if (status === 200 && json?.success !== false) {
      yield put(updateTeamsManagementTeams(Array.isArray(json?.data) ? json.data : []))
      yield put(updateTeamsManagementCountrySites(json?.meta?.country_sites ?? []))
    } else {
      yield put(updateTeamsManagementTeamsError(json as unknown as Record<string, unknown>))
    }
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { context: 'loadTeamsSaga' } })
    }
    const message = error instanceof Error ? error.message : 'Unknown error'
    yield put(updateTeamsManagementTeamsError({ error: message }))
  } finally {
    yield put(updateTeamsManagementIsLoadingTeams(false))
  }
}

export function* updateTeam(action: any): Generator {
  const { teamData, updateTeamSuccessNotification, setFormErrors } = action
  try {
    const { authTokenSession, lang, extraRoutes } = (yield select(selectAuthData)) as AuthData
    const updateRoute = extraRoutes?.teams?.update?.replace('*', String(teamData.id))
    if (!updateRoute) return

    const config = getAPIConfig({ method: 'put', authTokenSession, body: teamData })
    const response = (yield doFetch(buildUrl(updateRoute, lang), config)) as Response

    const { success, errors = {}, message = '' } = (yield call([response, response.json])) as TeamApiResponse

    if (success) {
      yield call(updateTeamSuccessNotification, message)
      yield call(loadTeamsSaga)
    } else {
      if (Object.keys(errors).length === 0 && typeof message === 'string') {
        alert(message)
      }
      yield call(setFormErrors, errors)
    }
  } catch (error) {
    yield call(setFormErrors, error)
  }
}

export function* createTeam(action: any): Generator {
  const { teamData, updateTeamSuccessNotification, setFormErrors } = action
  try {
    const { authTokenSession, lang, extraRoutes } = (yield select(selectAuthData)) as AuthData
    const createRoute = extraRoutes?.teams?.create
    if (!createRoute) return

    const config = getAPIConfig({ method: 'post', authTokenSession, body: teamData })
    const response = (yield doFetch(buildUrl(createRoute, lang), config)) as Response

    const { success, errors = {}, message = '' } = (yield call([response, response.json])) as TeamApiResponse

    if (success) {
      yield call(updateTeamSuccessNotification, message)
      yield call(loadTeamsSaga)
    } else {
      if (Object.keys(errors).length === 0 && typeof message === 'string') {
        alert(message)
      }
      yield call(setFormErrors, errors)
    }
  } catch (error) {
    yield call(setFormErrors, error)
  }
}

export function* deleteTeam(action: any): Generator {
  const { teamId } = action
  try {
    const { authTokenSession, lang, extraRoutes } = (yield select(selectAuthData)) as AuthData
    const deleteRoute = extraRoutes?.teams?.delete?.replace('*', String(teamId))
    if (!deleteRoute) return

    const config = getAPIConfig({ method: 'delete', authTokenSession, body: {} })
    const response = (yield doFetch(buildUrl(deleteRoute, lang), config)) as Response
    const { success } = (yield call([response, response.json])) as TeamApiResponse
    if (!success) return
    yield call(loadTeamsSaga)
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { context: 'deleteTeamSaga' } })
    }
  }
}

export default function* teamsManagementSagas() {
  yield takeLatest(teamsManagementActions.LOAD_LIST, loadTeamsSaga)
  yield takeLatest(teamsManagementActions.UPDATE_TEAM, updateTeam)
  yield takeLatest(teamsManagementActions.CREATE_TEAM, createTeam)
  yield takeLatest(teamsManagementActions.DELETE_TEAM, deleteTeam)
}
